/**
 * Voice prompts — text-to-speech announcements for the staff app.
 *
 * Uses the Web Speech API (window.speechSynthesis). Works inside the
 * Capacitor WebView on Android using the system TTS engine, and inside
 * any modern desktop / mobile browser.
 *
 * On Android, voices are loaded by the system TTS engine. UK English
 * female voices are usually available out of the box ("Google UK English
 * Female" on most devices); we fall back gracefully if not.
 *
 * Public API:
 *   speak(text, opts?) — fires-and-forgets one utterance with the best
 *                        available UK female English voice.
 *   primeTts()        — call once on a user gesture to unblock autoplay
 *                        restrictions (silent utterance, no audio).
 */

let _voicesCache = null;

function _allVoices() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return [];
  if (_voicesCache && _voicesCache.length) return _voicesCache;
  try {
    _voicesCache = window.speechSynthesis.getVoices() || [];
  } catch {
    _voicesCache = [];
  }
  return _voicesCache;
}

// Refresh the cache whenever the system announces voices have changed.
if (typeof window !== 'undefined' && window.speechSynthesis) {
  try {
    window.speechSynthesis.addEventListener('voiceschanged', () => {
      _voicesCache = window.speechSynthesis.getVoices() || [];
    });
  } catch {}
}

/**
 * Pick the best available UK English female voice.
 * Preference order:
 *   1. en-GB voice whose name hints at "female" / "Kate" / "Hazel" / "Susan" /
 *      "Amy" (these are the common Android/Chrome UK female TTS voice names).
 *   2. Any en-GB voice (default-localized UK).
 *   3. Any en-* voice whose name hints at "female".
 *   4. Any voice at all (last resort — system default).
 */
function _pickUkFemaleVoice() {
  const voices = _allVoices();
  if (!voices.length) return null;
  const ukFemale = voices.find((v) =>
    /en-GB/i.test(v.lang) &&
    /(female|kate|hazel|susan|amy|libby|sonia|google uk english female)/i.test(v.name)
  );
  if (ukFemale) return ukFemale;
  const ukAny = voices.find((v) => /en-GB/i.test(v.lang));
  if (ukAny) return ukAny;
  const anyFemale = voices.find((v) =>
    /^en/i.test(v.lang) && /(female|samantha|tessa|karen|moira|fiona)/i.test(v.name)
  );
  if (anyFemale) return anyFemale;
  return voices[0] || null;
}

/**
 * Speak `text` using the best UK female voice we can find.
 *
 * Options:
 *   rate    — 0.5 (slow) … 2.0 (fast). Default 0.95 (calm).
 *   pitch   — 0 … 2. Default 1.0.
 *   volume  — 0 … 1. Default 1.0.
 *   interrupt — when true (default), cancel anything already speaking so the
 *               most recent overrun reminder isn't queued behind a stale one.
 */
export function speak(text, opts = {}) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  if (!text) return;
  try {
    if (opts.interrupt !== false) {
      try { window.speechSynthesis.cancel(); } catch {}
    }
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = 'en-GB';
    u.rate = typeof opts.rate === 'number' ? opts.rate : 0.95;
    u.pitch = typeof opts.pitch === 'number' ? opts.pitch : 1.0;
    u.volume = typeof opts.volume === 'number' ? opts.volume : 1.0;
    const v = _pickUkFemaleVoice();
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  } catch {}
}

/**
 * Browsers / Android WebViews block speech synthesis until they see a user
 * gesture. Call this from a button click during onboarding (or after login)
 * to "prime" the engine with a silent utterance so later autonomous prompts
 * (e.g., a 60-second overrun reminder) actually play through.
 */
export function primeTts() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    const u = new window.SpeechSynthesisUtterance(' ');
    u.volume = 0;
    u.rate = 1;
    window.speechSynthesis.speak(u);
  } catch {}
}
